import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Banknote } from 'lucide-react';
import { Page, PageBody, PageHeader, Stat, StatRow, Toolbar } from '@/ui/Panel';
import { Callout } from '@/ui/Callout';
import { DataTable, type Column } from '@/ui/DataTable';
import { ChipGroup } from '@/ui/Filters';
import { Select } from '@/ui/Field';
import { Badge } from '@/ui/Badge';
import { api } from '@/lib/api/client';
import { useShortcutHint } from '@/app/StatusLine';
import { dateTime, money, num, smartDate } from '@/lib/format';
import type { CashShift, RegisterSummary, UserSummary } from '@/lib/api/types';

/**
 * Drawer shift history — deliberately read-only.
 *
 * A shift is a physical fact about a cash drawer: it is opened, counted and
 * closed at the lane, and arrives here through sync-up. The API does expose
 * create/reconcile routes, but wiring them into the console would let a manager
 * mint a drawer count from a desk, which is exactly the thing a drawer count is
 * supposed to prove. So this screen reports and never writes, and says so.
 *
 * `GET /cash/shifts` returns bare `cash_shifts` rows with no joins, so register
 * and cashier names are resolved here from `/registers` and `/users`.
 */

type StatusFilter = 'all' | 'open' | 'closed';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
];

interface CashSearch {
  status: StatusFilter;
  register?: number;
}

function optionalId(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

/** Over is not automatically good — a drawer that is over is still wrong. */
function varianceTone(variance: number): 'positive' | 'warn' | 'negative' {
  if (variance === 0) return 'positive';
  return variance > 0 ? 'warn' : 'negative';
}

export const Route = createFileRoute('/_app/cash')({
  staticData: { crumb: 'Cash' },
  validateSearch: (s: Record<string, unknown>): CashSearch => ({
    status: STATUS_OPTIONS.some((o) => o.value === s.status)
      ? (s.status as StatusFilter)
      : 'all',
    register: optionalId(s.register),
  }),
  component: CashScreen,
});

function CashScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  useShortcutHint([{ keys: 'j / k', label: 'Move' }]);

  function patch(next: Partial<CashSearch>) {
    void navigate({ search: (prev: CashSearch) => ({ ...prev, ...next }) });
  }

  const shifts = useQuery({
    queryKey: ['cash', 'shifts'],
    queryFn: () => api.get<CashShift[]>('/cash/shifts'),
  });

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: () => api.get<RegisterSummary[]>('/registers'),
    staleTime: 5 * 60_000,
  });

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/users'),
    staleTime: 5 * 60_000,
  });

  const registerName = useMemo(() => {
    const map = new Map<number, string>();
    for (const r of registers.data ?? []) map.set(r.id, r.name);
    return map;
  }, [registers.data]);

  const cashierName = useMemo(() => {
    const map = new Map<number, string>();
    for (const u of users.data ?? []) {
      map.set(u.id, `${u.firstName} ${u.lastName}`.trim() || u.username);
    }
    return map;
  }, [users.data]);

  const rows = useMemo(() => {
    const all = shifts.data;
    if (!all) return undefined;
    return (
      all
        .filter((s) => (search.status === 'all' ? true : s.status === search.status))
        .filter((s) => (search.register ? s.registerId === search.register : true))
        // The endpoint applies no ordering at all, so newest-first happens here.
        .slice()
        .sort((a, b) => new Date(b.openedAt).getTime() - new Date(a.openedAt).getTime())
    );
  }, [shifts.data, search.status, search.register]);

  const totals = useMemo(() => {
    const all = rows ?? [];
    const counted = all.filter(
      (s) => s.countedDrawer !== null && s.countedDrawer !== undefined
    );
    return {
      open: all.filter((s) => s.status === 'open').length,
      counted: counted.length,
      net: counted.reduce((sum, s) => sum + (s.variance ?? 0), 0),
      short: counted.filter((s) => (s.variance ?? 0) < 0).length,
    };
  }, [rows]);

  const columns: Array<Column<CashShift>> = [
    {
      id: 'openedAt',
      header: 'Opened',
      width: '7rem',
      mono: true,
      cell: (row) => (
        <span title={dateTime(row.openedAt)}>{smartDate(row.openedAt)}</span>
      ),
    },
    {
      id: 'closedAt',
      header: 'Closed',
      width: '7rem',
      mono: true,
      hideBelow: 'md',
      cell: (row) =>
        row.closedAt ? (
          <span title={dateTime(row.closedAt)}>{smartDate(row.closedAt)}</span>
        ) : (
          <Badge tone="signal" dot pulse>
            Running
          </Badge>
        ),
    },
    {
      id: 'register',
      header: 'Register',
      width: '9rem',
      cell: (row) => (
        <span className="text-ink truncate text-xs">
          {registerName.get(row.registerId) ?? `Register ${row.registerId}`}
        </span>
      ),
    },
    {
      id: 'cashier',
      header: 'Cashier',
      hideBelow: 'lg',
      cell: (row) => (
        <span className="text-ink-muted truncate text-xs">
          {row.cashierId == null
            ? 'Unassigned'
            : (cashierName.get(row.cashierId) ?? `User ${row.cashierId}`)}
        </span>
      ),
    },
    {
      id: 'openingFloat',
      header: 'Float',
      width: '6.5rem',
      align: 'right',
      mono: true,
      hideBelow: 'xl',
      cell: (row) => money(row.openingFloat),
    },
    {
      id: 'expectedDrawer',
      header: 'Expected',
      width: '7rem',
      align: 'right',
      mono: true,
      cell: (row) => money(row.expectedDrawer),
    },
    {
      id: 'countedDrawer',
      header: 'Counted',
      width: '7rem',
      align: 'right',
      mono: true,
      cell: (row) =>
        row.countedDrawer == null ? (
          <span className="text-ink-faint">—</span>
        ) : (
          money(row.countedDrawer)
        ),
    },
    {
      id: 'variance',
      header: 'Variance',
      width: '8.5rem',
      align: 'right',
      mono: true,
      cell: (row) => {
        if (row.countedDrawer == null || row.variance == null) {
          return <span className="text-ink-faint">not counted</span>;
        }
        const tone = varianceTone(row.variance);
        return (
          <span
            className={
              tone === 'positive'
                ? 'text-positive'
                : tone === 'warn'
                  ? 'text-warn'
                  : 'text-negative'
            }
          >
            {row.variance === 0
              ? 'balanced'
              : `${money(row.variance)} ${row.variance > 0 ? 'over' : 'short'}`}
          </span>
        );
      },
    },
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="Selling"
        title="Cash drawers"
        subtitle="Shift history as reported by the registers"
      />

      <Toolbar>
        <ChipGroup<StatusFilter>
          value={search.status}
          onChange={(v) => patch({ status: v })}
          options={STATUS_OPTIONS}
        />
        <div className="bg-line h-4 w-px" />
        <Select
          size="sm"
          className="w-44"
          aria-label="Register"
          value={search.register === undefined ? '' : String(search.register)}
          onChange={(e) => patch({ register: optionalId(e.currentTarget.value) })}
        >
          <option value="">Every register</option>
          {(registers.data ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Select>
        <span className="text-2xs text-ink-faint ml-auto">
          {rows ? `${num(rows.length)} shifts` : ''}
        </span>
      </Toolbar>

      <div className="border-line shrink-0 border-b px-4 py-3">
        <StatRow className="grid-cols-2 md:grid-cols-4 xl:grid-cols-4">
          <Stat
            label="Running now"
            value={num(totals.open)}
            sub="Drawers still open"
            tone={totals.open > 0 ? 'signal' : undefined}
          />
          <Stat label="Counted" value={num(totals.counted)} />
          <Stat
            label="Net variance"
            value={money(totals.net)}
            sub="Counted shifts in view"
            tone={totals.net === 0 ? undefined : totals.net < 0 ? 'negative' : 'warn'}
          />
          <Stat
            label="Short"
            value={num(totals.short)}
            sub="Counted under expected"
            tone={totals.short > 0 ? 'negative' : undefined}
          />
        </StatRow>
      </div>

      <PageBody bleed className="flex min-h-0 flex-col overflow-hidden">
        <div className="shrink-0 px-4 pt-3">
          <Callout tone="info" title="Read-only on purpose">
            A shift is a physical fact about a drawer: it is opened, counted and closed at
            the register, then arrives here through sync-up. Opening or reconciling a
            drawer from a desk would forge the very count it is meant to prove, so this
            console reports shifts and never writes them. Corrections belong at the lane.
          </Callout>
        </div>
        <div className="mt-3 min-h-0 flex-1">
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            loading={shifts.isLoading}
            error={shifts.error}
            onRetry={() => void shifts.refetch()}
            rowTone={(row) =>
              row.countedDrawer != null && (row.variance ?? 0) < 0 ? 'negative' : null
            }
            empty={{
              icon: <Banknote />,
              title: 'No shifts in this view',
              description:
                'Shifts appear once a register opens a drawer and syncs up. Try clearing the filters.',
            }}
            footer={
              <span>
                {num(rows?.length ?? 0)} shifts · variance is counted minus expected
              </span>
            }
          />
        </div>
      </PageBody>
    </Page>
  );
}
